import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import API_BASE_URL from "../../src/config";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "./Sidebar";
import { ClipLoader } from "react-spinners";
import { formatLocalDate, parseLocalDate } from "../utils/dateUtils";

const Profile = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({}); //. validation errors

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [profileImage, setProfileImage] = useState(null);


  // State for password messages
  const [messagePassword, setMessagePassword] = useState("");
  const [pageLoading, setPageLoading] = useState(true);



  // ---------- Change Password ----------
  const handleChangePassword = async () => {
    const newErrors = {};
    if (!user?.currentPassword) newErrors.currentPassword = "Current password is required";
    if (!user?.newPassword) newErrors.newPassword = "New password is required";
    if (!user?.confirmPassword) newErrors.confirmPassword = "Confirm your new password";
    if (user?.newPassword && user?.confirmPassword && user.newPassword !== user.confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    setMessagePassword("");

    try {
      const token = localStorage.getItem("accessToken") || "";
      if (!token) {
        setMessagePassword("❌ Access token missing. Please login again.");
        setLoading(false);
        return;
      }

      const payload = {
        currentPassword: user.currentPassword,
        newPassword: user.newPassword,
      };

      const response = await axios.post(`${API_BASE_URL}auth/change-password`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.data?.success) {
        setMessagePassword("✅ Password changed successfully!");
        setUser((u) => ({ ...u, currentPassword: "", newPassword: "", confirmPassword: "" }));
      } else {
        setMessagePassword(`❌ ${response?.data?.message || "Failed to change password."}`);
      }
    } catch (err) {
      console.error("Change password error:", err);
      setMessagePassword("❌ Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  // ---------- Helpers ----------
  const fetchStates = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}user/states`);
      if (res?.data?.success) {
        const options = res.data.data
  .map((st) => ({
    value: st.id,
    label: st.name,
  }))
  .sort((a, b) => a.label.localeCompare(b.label)); //. alphabetical order
        setStates(options);
        return options;
      }
      return [];
    } catch (err) {
      console.error("Error fetching states:", err);
      return [];
    }
  };

  const fetchCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return [];
    }
    try {
      const res = await axios.get(`${API_BASE_URL}user/cities/${stateId}`);
      if (res?.data?.success) {
        const options = res.data.data.map((ct) => ({
          value: ct.name,
          label: ct.name,
        }));
        setCities(options);
        return options;
      }
      setCities([]);
      return [];
    } catch (err) {
      console.error("Error fetching cities:", err);
      setCities([]);
      return [];
    }
  };

  // ---------- Mount ----------
  useEffect(() => {
    (async () => {
      setPageLoading(true);
      const stateOptions = await fetchStates();
      const stored = localStorage.getItem("user");
      if (!stored) return;

      let parsed = JSON.parse(stored);
      let resolvedStateId = parsed.state_id ?? null;

      if (!resolvedStateId && parsed.state && stateOptions.length) {
        const matched = stateOptions.find(
          (s) =>
            String(s.label).toLowerCase() === String(parsed.state).toLowerCase() ||
            String(s.value) === String(parsed.state)
        );
        if (matched) resolvedStateId = matched.value;
      }

      if (resolvedStateId) {
        const matchedState = stateOptions.find(
          (s) => String(s.value) === String(resolvedStateId)
        );
        parsed = {
          ...parsed,
          state_id: resolvedStateId,
          state: parsed.state || matchedState?.label || parsed.state,
        };

        const cityOptions = await fetchCities(resolvedStateId);
        if (parsed.city) {
          const foundCity = cityOptions.find(
            (c) => String(c.value).toLowerCase() === String(parsed.city).toLowerCase()
          );
          if (!foundCity) {
            setCities((prev) => [{ value: parsed.city, label: parsed.city }, ...prev]);
          }
        }
      }

      setUser(parsed);
      setPageLoading(false);
    })();
  }, []);

  // ---------- Logout ----------
  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    navigate("/");
  };

  // ---------- Validation ----------
  const validateForm = () => {
    const newErrors = {};
    if (!user?.firstname) newErrors.firstname = "First name is required";
    if (!user?.lastname) newErrors.lastname = "Last name is required";
    if (!user?.gender) newErrors.gender = "Gender is required";
    if (!user?.dob) newErrors.dob = "Date of birth is required";
    if (!user?.state && !user?.state_id) newErrors.state = "State is required";
    if (!user?.city) newErrors.city = "City is required";
    if (!user?.address_line1) newErrors.address_line1 = "Address is required";
    if (!user?.pincode) newErrors.pincode = "Pincode is required";
    // else if (!/^\d{6}$/.test(user.pincode)) newErrors.pincode = "Invalid pincode";
    if (!user?.phone) newErrors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(user.phone)) newErrors.phone = "Invalid phone number";
    if (!user?.email) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email))
      newErrors.email = "Invalid email address";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---------- Update profile ----------
  const handleUpdate = async () => {
    if (!user) return;

    //. Validate before submit
    if (!validateForm()) return;

    setLoading(true);
    setMessage("");

    try {
      const payload = {
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        phone: user.phone,
        city: user.city,
        state: user.state,
        state_id: user.state_id ?? null,
        gender: user.gender,
        dob: user.dob,
        address_line1: user.address_line1,
        address_line2: user.address_line2,
        pincode: user.pincode,
      };

      // const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      // const token = localStorage.getItem("accessToken") || "";


      // if (!token) {
      //   setMessage("❌ Access token missing. Please login again.");
      //   setLoading(false);
      //   return;
      // }

      // const response = await axios.post(`${API_BASE_URL}auth/updateprofile`, payload, {
      //   headers: {
      //     "Content-Type": "application/json",
      //     Authorization: `Bearer ${token}`,
      //   },
      // });


      // Get stored user safely
      const storedUserRaw = localStorage.getItem("user");
      const storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null;

      // Get token: prefer storedUser.accessToken if it exists, otherwise localStorage token
      const token = storedUser?.accessToken || localStorage.getItem("accessToken") || "";

      // Debug
      console.log("storedUser:", storedUser);
      console.log("token used for Authorization:", token);

      // Guard: require a token before calling API
      if (!token) {
        setMessage("❌ Access token missing. Please login again.");
        setLoading(false);
        return;
      }

      console.log("token:", token);
      const response = await axios.post(
        `${API_BASE_URL}auth/updateprofile`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, //. token from either source
          },
        }
      );

      if (response?.data?.success) {
        const serverUser = response.data.data || {};
        const merged = {
          ...serverUser,
          state_id: serverUser.state_id ?? user.state_id ?? null,
          state: serverUser.state ?? user.state ?? null,
          city: serverUser.city ?? user.city ?? null,
        };

        localStorage.setItem("user", JSON.stringify(merged));
        setUser(merged);

        if (merged.state_id) {
          await fetchCities(merged.state_id);
        } else if (merged.state) {
          const matched = states.find(
            (s) => String(s.label).toLowerCase() === String(merged.state).toLowerCase()
          );
          if (matched) await fetchCities(matched.value);
        }

        // setMessage("✅ Profile updated successfully!");
        toast.success("Profile updated successfully!");
      } else {
        setMessage(`❌ ${response?.data?.message || "Failed to update profile."}`);
      }
    } catch (error) {
      console.error("Update error:", error);
      setMessage("❌ Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Handlers ----------
  const handleStateChange = async (option) => {
    if (!option) {
      setUser((u) => ({ ...u, state: "", state_id: null, city: "" }));
      setCities([]);
      return;
    }
    setUser((u) => ({ ...u, state: option.label, state_id: option.value, city: "" }));
    await fetchCities(option.value);
  };

  const handleCityChange = (option) => {
    setUser((u) => ({ ...u, city: option ? option.value : "" }));
  };
  if (pageLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <ClipLoader size={45} />
      </div>
    );
  }
  // ---------- No user ----------
  if (!user) {
    return (
      <section className="profilecontainer">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <h3>No user data found. Please log in.</h3>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ---------- react-select values ----------
  const selectedStateOption =
    states.find((s) => String(s.value) === String(user.state_id)) ||
    (user.state ? { value: user.state_id ?? user.state, label: user.state } : null);

  const selectedCityOption =
    cities.find((c) => String(c.value).toLowerCase() === String(user.city).toLowerCase()) ||
    (user.city ? { value: user.city, label: user.city } : null);


  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setProfileImage(URL.createObjectURL(file));

    const formData = new FormData();
    formData.append("profile_image", file);

    const token =
      JSON.parse(localStorage.getItem("user") || "{}")?.accessToken ||
      localStorage.getItem("accessToken") ||
      "";

    if (!token) {
      toast.error("Access token missing. Please login again.");
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}auth/update-profile-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.data?.success) {
        const updated = { ...user, profile_image: response.data.data.profile_image };
        localStorage.setItem("user", JSON.stringify(updated));
        setUser(updated);

        toast.success("Profile image updated successfully!");
        // setTimeout(() => {
        //   window.location.reload();
        // }, 800);

      } else {
        toast.error(response?.data?.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Something went wrong while uploading.");
    }
  };


  return (
    <section className="profilecontainer">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="profileinner">
              <div className="row">
                {/* Left Navigation */}
                <Sidebar
                  user={user}
                  profileImage={profileImage}
                  selectedStateOption={selectedStateOption}
                  selectedCityOption={selectedCityOption}
                  onImageUpload={handleProfileImageUpload}
                />

                {/* Right Content */}
                <div className="col-xxl-9 col-xl-9 col-lg-8 col-md-8 col-sm-12 col-12">
                  <div className="profilerightouter">
                    <div className="profileimageupload">
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div className="profileimg">
                          <label for="userProfile" className="change-photo-btn" >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-camera-icon lucide-camera"><path d="M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z" /><circle cx="12" cy="13" r="3" /></svg>
                            <input type="file" className="d-none" id="userProfile" accept="image/*" onChange={handleProfileImageUpload} hidden />
                          </label>
                          <img src={profileImage || user?.profile_image || "images/pro.png"} alt="Profile" />
                        </div>
                        <div className="profilertcontent">
                          <div className="profilename">{user.firstname} {user.lastname}</div>
                          <p>{user.state}</p>
                        </div>
                      </div>
                      <p className="upload-hint mt-2">JPG, PNG • Max 5MB</p>
                    </div>
                    <h3>General Information</h3>
                    <div className="row gx-2 gx-lg-4">
                      {/* Firstname */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>First Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={user?.firstname || ""}
                            onChange={(e) =>
                              setUser((u) => ({ ...u, firstname: e.target.value }))
                            }
                          />
                          {errors.firstname && (
                            <small className="text-danger">{errors.firstname}</small>
                          )}
                        </div>
                      </div>

                      {/* Lastname */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Last Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={user?.lastname || ""}
                            onChange={(e) =>
                              setUser((u) => ({ ...u, lastname: e.target.value }))
                            }
                          />
                          {errors.lastname && (
                            <small className="text-danger">{errors.lastname}</small>
                          )}
                        </div>
                      </div>

                      {/* Gender */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Gender *</label>
                          <select
                            className="form-select"
                            value={user?.gender || ""}
                            onChange={(e) =>
                              setUser((u) => ({ ...u, gender: e.target.value }))
                            }
                          >
                            <option value="">Select</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                          {errors.gender && (
                            <small className="text-danger">{errors.gender}</small>
                          )}
                        </div>
                      </div>

                      {/* DOB */}
                      {/* <div className="col-md-6">
                        <div className="form-group">
                          <label>Date of Birth</label>
                          <DatePicker
                            selected={user?.dob ? new Date(user.dob) : null}
                            onChange={(date) =>
                              setUser((u) => ({
                                ...u,
                                dob: date ? date.toISOString().split("T")[0] : "",
                              }))
                            }
                            dateFormat="dd/MM/yyyy"
                            showYearDropdown
                            scrollableYearDropdown
                            maxDate={new Date()}
                            placeholderText="Select your date of birth"
                            className="form-control"
                          />
                          {errors.dob && (
                            <small className="text-danger">{errors.dob}</small>
                          )}
                        </div>
                      </div> */}

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Date of Birth *</label>

                          {/* Age calculation for max allowed date */}
                          {(() => {
                            var eighteenYearsAgo = new Date();
                            eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);

                            return (
                              <DatePicker
                                selected={user?.dob ? parseLocalDate(user.dob) : null}
                                onChange={(date) =>
                                  setUser((prev) => ({
                                    ...prev,
                                    dob: date ? formatLocalDate(date) : "",
                                  }))
                                }
                                dateFormat="dd/MM/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                scrollableYearDropdown
                                yearDropdownItemNumber={100}
                                maxDate={eighteenYearsAgo} // 🚨 Enforce 18+ age rule
                                placeholderText="Select your date of birth"
                                className="form-control"
                              />
                            );
                          })()}
                          {errors.dob && <p className="text-danger">{errors.dob}</p>}
                        </div>
                      </div>



                      {/* State */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>State *</label>
                          <Select
                            options={states}
                            value={selectedStateOption}
                            onChange={handleStateChange}
                            placeholder="Select State"
                            isSearchable
                          // menuIsOpen={true}
                          />
                          {errors.state && (
                            <small className="text-danger">{errors.state}</small>
                          )}
                        </div>
                      </div>

                      {/* City */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>City of Residence *</label>
                          <Select
                            options={cities}
                            value={selectedCityOption}
                            onChange={handleCityChange}
                            placeholder="Select City"
                            isSearchable
                            isDisabled={!user?.state_id && !user?.state}
                          />
                          {errors.city && (
                            <small className="text-danger">{errors.city}</small>
                          )}
                        </div>
                      </div>

                      {/* Address */}
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>Address *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={user?.address_line1 || ""}
                            onChange={(e) =>
                              setUser((u) => ({ ...u, address_line1: e.target.value }))
                            }
                            placeholder="Enter your address"
                          />
                          {errors.address_line1 && (
                            <small className="text-danger">{errors.address_line1}</small>
                          )}
                        </div>
                      </div>

                      {/* Address 2 */}
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>Address 2</label>
                          <input
                            type="text"
                            className="form-control"
                            value={user?.address_line2 || ""}
                            onChange={(e) =>
                              setUser((u) => ({ ...u, address_line2: e.target.value }))
                            }
                            placeholder="Apartment, Suite, Landmark (optional)"
                          />
                        </div>
                      </div>

                      {/* Pincode */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Pincode *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={user?.pincode || ""}
                            maxLength={6}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              setUser((u) => ({ ...u, pincode: value }));
                            }}
                            placeholder="Enter your area pincode"
                          />
                          {errors.pincode && (
                            <small className="text-danger">{errors.pincode}</small>
                          )}
                        </div>
                      </div>


                      {/* Phone */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Mobile Number *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={user?.phone || ""}
                            onChange={(e) =>
                              setUser((u) => ({ ...u, phone: e.target.value }))
                            }
                          />
                          {errors.phone && (
                            <small className="text-danger">{errors.phone}</small>
                          )}
                        </div>
                      </div>

                      {/* Email */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Email Id *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={user?.email || ""}
                            onChange={(e) =>
                              setUser((u) => ({ ...u, email: e.target.value }))
                            }
                          />
                          {errors.email && (
                            <small className="text-danger">{errors.email}</small>
                          )}
                        </div>
                      </div>

                      {/* Update Button */}
                      <div className="col-12">
                        <button
                          className="theme-btn"
                          onClick={handleUpdate}
                          disabled={loading}
                        >
                          {loading ? "Updating..." : "Update Profile"}
                        </button>
                        {/* {message && (
                          <p className="mt-2">
                            <strong>{message}</strong>
                          </p>
                        )} */}
                      </div>

                      {/* Change Password Section */}
                      <div className="mt-4">
                        <h3>Change Password</h3>
                        <input type="text" autoComplete="username" style={{ display: "none" }} />
                        <input type="password" autoComplete="current-password" style={{ display: "none" }} />

                        <div className="row gx-2 gx-lg-4">
                          {/* Current Password */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Current Password</label>
                              <input
                                type="password"
                                name="current_password"
                                autoComplete="new-password"
                                className="form-control"
                                value={user?.currentPassword || ""}
                                onChange={(e) =>
                                  setUser((u) => ({ ...u, currentPassword: e.target.value }))
                                }
                              />
                              {errors.currentPassword && (
                                <small className="text-danger">{errors.currentPassword}</small>
                              )}
                            </div>
                          </div>

                          {/* New Password */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>New Password</label>
                              <input
                                type="password"
                                name="new_password"
                                autoComplete="new-password"
                                className="form-control"
                                value={user?.newPassword || ""}
                                onChange={(e) =>
                                  setUser((u) => ({ ...u, newPassword: e.target.value }))
                                }
                              />
                              {errors.newPassword && (
                                <small className="text-danger">{errors.newPassword}</small>
                              )}
                            </div>
                          </div>

                          {/* Confirm Password */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Confirm New Password</label>
                              <input
                                type="password"
                                name="confirm_password"
                                autoComplete="new-password"
                                className="form-control"
                                value={user?.confirmPassword || ""}
                                onChange={(e) =>
                                  setUser((u) => ({ ...u, confirmPassword: e.target.value }))
                                }
                              />
                              {errors.confirmPassword && (
                                <small className="text-danger">{errors.confirmPassword}</small>
                              )}
                            </div>
                          </div>

                          {/* Update Password Button */}
                          <div className="col-12">
                            <button
                              className="theme-btn"
                              onClick={handleChangePassword}
                              disabled={loading}
                            >
                              {loading ? "Updating..." : "Change Password"}
                            </button>
                            {messagePassword && (
                              <p className="mt-2">
                                <strong>{messagePassword}</strong>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>


                {/* End Right Content */}
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>


  );
};

export default Profile;